import React from 'react';
import classNames from 'classnames';
import * as FormProperties from './properties-config';
import { FormFieldBase, getFieldProperties, getFieldPropertyValues } from '.';
import { FormFieldInputCheckbox } from './form-field-inputs';
import ValidationMessage from './validation-message';
import { getOptionValue as getOptionValueHelper } from './helpers';

export const FormFieldMultiple = ({
  field,
  className,
  commonProps,
  isRenderedField,
  onChange,
  value,
  onBlur,
  validationMessage,
  readOnly,
}) => {
  const getOptionValue = (option, value) => {
    if (!isRenderedField || !value) return;

    const optionValue = getOptionValueHelper(option, 'alt');
    const valueItem = value.find(val => Object.keys(val)[0] === optionValue);

    if (!valueItem) {
      return false;
    }

    return valueItem[optionValue];
  };

  const handleChange = (e, group, label, otherEl, otherIndex) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

    // define custom event object
    const event = {
      target: {
        id: group,
        value: { [`${label}`]: value },
        el: e.target,
        otherEl, // refers to the <input type="text"/> element of the "other" option, if present
        useIndexOverride: !!otherIndex,
        index: otherIndex,
      },
    };

    onChange(event);
  };

  const handleBlur = () => {
    onBlur({
      target: {
        id: field.uuid,
        value,
      },
    });
  };

  const localProps = new Map(FormProperties.Multiple);
  const propertyNames = ['options', 'displayOther', 'other', 'displayAsColumns', 'numColumns'];
  const defaultValues = propertyNames.map(propName => ({
    [`${propName}`]: localProps.get(propName).defaultValue,
  }));
  const properties = getFieldProperties(field, 'specific');
  const { options, displayOther, other, displayAsColumns, numColumns } = getFieldPropertyValues(
    properties.specific,
    propertyNames,
    defaultValues,
  );
  const classes = classNames({
    [className]: true,
    multiple: true,
    'control-group error': validationMessage,
  });

  // do not show the border red for 'Other' option if field has an error
  const otherCheckboxClasses = classNames({
    other: true,
    'control-group form-field-multiple overwrite-error': true,
  });

  // if this field is being rendered to a published form,
  // we need to add a group prop to the checkboxes to tie
  // back to the parent field
  const group = isRenderedField ? field.uuid : undefined;
  const otherValue = isRenderedField && displayOther ? getOptionValue(other, value) : '';
  const otherIndex = displayOther && options.length;

  return (
    <div className={classes}>
      <div
        className="form-field-label primary"
      >
        {`${commonProps.label} ${commonProps.makeRequired ? '*' : ''}`}
      </div>

      <div 
        className="form-field-input"
        style={{
          gap: '1rem',
          display: displayAsColumns ? 'block' : 'flex',
          flexDirection: displayAsColumns ? 'column' : 'row',
          flexWrap: displayAsColumns ? 'nowrap' : 'wrap',
        }}
      >
        {options.map((option, index) => {
          const label = option.hasOwnProperty('src') ? (
            <img src={option.src} height={option.height} width={option.width} alt={option.alt} />
          ) : (
            option
          );
          // render radio input for each option
          return (
            <FormFieldInputCheckbox
              key={index}
              label={label}
              displayAsColumns={displayAsColumns}
              numColumns={numColumns}
              group={group}
              isRenderedField={isRenderedField}
              onChange={handleChange}
              checked={getOptionValue(option, value)}
              onBlur={handleBlur}
              disabled={readOnly}
            />
          );
        })}
        {displayOther && (
          // render an 'Other' option if selected
          <FormFieldInputCheckbox
            className={otherCheckboxClasses}
            isOther
            label={other}
            displayAsColumns={displayAsColumns}
            numColumns={numColumns}
            group={group}
            isRenderedField={isRenderedField}
            onChange={(e, group, value, otherEl) => {
              handleChange(e, group, value, otherEl, otherIndex);
            }}
            checked={otherValue}
            value={otherValue}
            onBlur={handleBlur}
            disabled={readOnly}
          />
        )}
        {commonProps.sublabel && (
          <div className='form-field-label secondary'>{commonProps.sublabel}</div>
        )}
        <ValidationMessage message={validationMessage} />
      </div>
    </div>
  );
};

FormFieldMultiple.defaultProps = {
  className: '',
  commonProps: {},
  isRenderedField: false,
  onChange: () => { },
  onBlur: () => { },
  readOnly: false,
};

export default FormFieldBase(FormFieldMultiple);
